"use client"

import { useMemo, type ComponentProps } from "react"

import { Link as LocaleLink, useRouter as useLocaleRouter } from "@/i18n/navigation-base"
import { useNavigationPath } from "@/platform/app-mode/NavigationProvider"

const navigationHref = <Href extends string | { pathname?: string | null }>(href: Href, navigationPath: (path: string) => string): Href => {
  if (typeof href === "string") return navigationPath(href) as Href
  const target = href as { pathname?: string | null; host?: string | null; hostname?: string | null; protocol?: string | null }
  if (target.host || target.hostname || target.protocol) return href
  return { ...target, ...(target.pathname ? { pathname: navigationPath(target.pathname) } : {}) } as Href
}

export const Link = ({ href, ...props }: ComponentProps<typeof LocaleLink>) => {
  const navigationPath = useNavigationPath()
  const target = navigationHref(href, navigationPath)
  return <LocaleLink {...props} href={target} />
}

export const useRouter = () => {
  const router = useLocaleRouter()
  const navigationPath = useNavigationPath()
  return useMemo(
    () => ({
      ...router,
      push: (href: Parameters<typeof router.push>[0], options?: Parameters<typeof router.push>[1]) => router.push(navigationHref(href, navigationPath), options),
      replace: (href: Parameters<typeof router.replace>[0], options?: Parameters<typeof router.replace>[1]) => router.replace(navigationHref(href, navigationPath), options),
      prefetch: (href: Parameters<typeof router.prefetch>[0], options?: Parameters<typeof router.prefetch>[1]) => router.prefetch(navigationHref(href, navigationPath), options),
    }),
    [router, navigationPath]
  )
}
